// Demo data for Dr. Sayuja Clinic. Every date is written as an offset from
// "today", so the dashboard always has appointments today, follow-ups due
// this week and revenue this month, whenever the demo is loaded or reset.

import type {
    Appointment,
    AppointmentStatus,
    ClinicData,
    FollowUp,
    FollowUpEvent,
    Gender,
    Invoice,
    Medicine,
    MessageKind,
    MessageLog,
    MessageStatus,
    Patient,
    PatientStatus,
    PaymentMethod,
    Prescription,
    Treatment,
    Visit
} from '@/types/clinic'

import {
    addDays,
    dayOfWeek,
    parseISODate,
    stampAt
} from '@/lib/dates'
import { getInvoiceSummary } from '@/lib/clinic/selectors'
import { buildTemplateValues, fillTemplate } from '@/lib/clinic/whatsapp'
import { formatInvoiceNumber, formatPatientCode, formatPrescriptionNumber } from '@/lib/clinic/codes'

import {
    DATA_VERSION,
    DEFAULT_SETTINGS,
    DEFAULT_TREATMENTS
} from '@/constants/clinic'

type Offset = number | `m${number}`

type TreatmentKey = 'scaling' | 'rct' | 'extraction' | 'filling' | 'consultation' | 'crown' | 'bridge' | 'whitening' | 'denture' | 'gum' | 'checkup' | 'other'

type MedicineKey = 'amox' | 'amoxClav' | 'clinda' | 'metro' | 'ibu' | 'ibuSos' | 'acecl' | 'panto' | 'chx' | 'chxPostExtraction' | 'desensitising'

type VisitKind =
    | 'checkup'
    | 'checkupBleeding'
    | 'checkupTravel'
    | 'checkupDenture'
    | 'checkupScaling'
    | 'scaling'
    | 'consultPulpitis'
    | 'consultAbscess'
    | 'consultSensitivity'
    | 'consultTrauma'
    | 'rct1'
    | 'rct2'
    | 'crown'
    | 'extraction'
    | 'extractionWisdom'
    | 'review'
    | 'filling'
    | 'gum'
    | 'whitening'
    | 'dentureImpression'
    | 'dentureFit'
    | 'bridge'

interface VisitTemplate {
    treatments: TreatmentKey[]
    complaint: string
    diagnosis: string
    notes: string
    charge?: boolean
    medicines?: MedicineKey[]
    instructions?: string
    prescriptionNotes?: string
}

interface SeedVisit {
    kind: VisitKind
    at: Offset
    tooth?: string
    quantity?: number
    diagnosis?: string
    pay?: 'full' | 'partial' | 'none'
    paid?: number
    method?: PaymentMethod
    balanceAt?: Offset
    discount?: number
    discountType?: 'amount' | 'percent'
    next?: Offset
    nextNote?: string
}

interface SeedPatient {
    name: string
    gender: Gender
    age?: number
    dob?: string
    place: string
    phone: string
    altPhone?: string
    address: string
    reg: Offset
    status: PatientStatus
    history: string
    allergies?: string
    conditions?: string
    notes?: string
    penicillinAllergy?: boolean
    visits: SeedVisit[]
}

interface SeedAppointment {
    patient: number
    at: Offset
    time: string
    treatment: TreatmentKey
    status: AppointmentStatus
    notes: string
}

interface SeedMessage {
    patient: number
    kind: MessageKind
    at: Offset
    time: string
    status: MessageStatus
    appointment?: number
    followUpOfPatient?: boolean
    invoiceOfPatient?: boolean
}

const POST_EXTRACTION_INSTRUCTIONS = 'Bite firmly on the gauze for 45 minutes.\nDo not spit, rinse or use a straw for 24 hours.\nHave cold, soft food today and avoid hot drinks.\nStart warm salt water rinses from tomorrow.\nCall the clinic if bleeding does not stop.'

const MEDICINES: Record<MedicineKey, Omit<Medicine, 'id'>> = {
    amox: { name: 'Amoxicillin 500 mg', dosage: '1 capsule', frequency: '1-1-1 (Three times daily)', duration: '5 days', instructions: 'After food' },
    amoxClav: { name: 'Amoxicillin + Clavulanic Acid 625 mg', dosage: '1 tablet', frequency: '1-0-1 (Twice daily)', duration: '5 days', instructions: 'After food' },
    clinda: { name: 'Clindamycin 300 mg', dosage: '1 capsule', frequency: '1-1-1 (Three times daily)', duration: '5 days', instructions: 'After food. Given due to penicillin allergy' },
    metro: { name: 'Metronidazole 400 mg', dosage: '1 tablet', frequency: '1-1-1 (Three times daily)', duration: '5 days', instructions: 'After food. Avoid alcohol' },
    ibu: { name: 'Ibuprofen 400 mg', dosage: '1 tablet', frequency: '1-0-1 (Twice daily)', duration: '3 days', instructions: 'After food' },
    ibuSos: { name: 'Ibuprofen 400 mg', dosage: '1 tablet', frequency: 'SOS (Only if needed)', duration: '3 days', instructions: 'After food, only if there is pain' },
    acecl: { name: 'Aceclofenac 100 mg + Paracetamol 325 mg', dosage: '1 tablet', frequency: '1-0-1 (Twice daily)', duration: '3 days', instructions: 'After food' },
    panto: { name: 'Pantoprazole 40 mg', dosage: '1 tablet', frequency: '1-0-0 (Morning)', duration: '5 days', instructions: 'Before breakfast' },
    chx: { name: 'Chlorhexidine Mouthwash 0.2%', dosage: '10 ml', frequency: 'Twice daily', duration: '7 days', instructions: 'Rinse for 30 seconds, do not swallow' },
    chxPostExtraction: { name: 'Chlorhexidine Mouthwash 0.2%', dosage: '10 ml', frequency: 'Twice daily', duration: '7 days', instructions: 'Start 24 hours after extraction. Rinse gently' },
    desensitising: { name: 'Desensitising Toothpaste (Potassium Nitrate 5%)', dosage: 'Pea sized amount', frequency: 'Twice daily', duration: '4 weeks', instructions: 'Brush gently, do not rinse immediately' }
}

const TEMPLATES: Record<VisitKind, VisitTemplate> = {
    checkup: {
        treatments: ['checkup'],
        complaint: 'Routine dental check-up',
        diagnosis: 'Mild plaque deposits, no active caries',
        notes: 'Oral hygiene instructions given. Advised cleaning every six months.'
    },
    checkupBleeding: {
        treatments: ['checkup'],
        complaint: 'Bleeding gums while brushing',
        diagnosis: 'Generalised chronic gingivitis',
        notes: 'Gum treatment advised. Soft brush and warm salt water rinses recommended.'
    },
    checkupTravel: {
        treatments: ['checkup'],
        complaint: 'Wants a full check-up before travelling abroad',
        diagnosis: 'Early caries on {tooth}, otherwise healthy',
        notes: 'Filling advised on {tooth} before travel.'
    },
    checkupDenture: {
        treatments: ['checkup'],
        complaint: 'Sore spot under the lower denture',
        diagnosis: 'Denture induced ulcer on the lower ridge',
        notes: 'Denture adjusted and polished. Review if the soreness continues.'
    },
    checkupScaling: {
        treatments: ['checkup', 'scaling'],
        complaint: 'Stains and tartar on lower front teeth',
        diagnosis: 'Generalised calculus with extrinsic stains',
        notes: 'Full mouth ultrasonic scaling and polishing done.',
        prescriptionNotes: 'Chlorhexidine mouthwash 0.2% twice daily for one week'
    },
    scaling: {
        treatments: ['scaling'],
        complaint: 'Six month cleaning visit',
        diagnosis: 'Moderate calculus on lower front teeth',
        notes: 'Scaling and polishing done. Gums are healthy.'
    },
    consultPulpitis: {
        treatments: ['consultation'],
        complaint: 'Severe throbbing pain in a lower back tooth for three days',
        diagnosis: 'Irreversible pulpitis in {tooth}',
        notes: 'X-ray taken. Root canal treatment advised and the cost explained.',
        medicines: ['amox', 'ibu', 'panto']
    },
    consultAbscess: {
        treatments: ['consultation'],
        complaint: 'Swelling and pain in the lower jaw since two days',
        diagnosis: 'Periapical abscess in {tooth}',
        notes: 'Swelling drained. Antibiotics started. Root canal treatment planned once the swelling settles.',
        medicines: ['amoxClav', 'metro', 'acecl']
    },
    consultSensitivity: {
        treatments: ['consultation'],
        complaint: 'Sensitivity to cold drinks in the upper teeth',
        diagnosis: 'Cervical abrasion with sensitivity on {tooth}',
        notes: 'Desensitising toothpaste advised. Fillings planned.',
        medicines: ['desensitising']
    },
    consultTrauma: {
        treatments: ['consultation'],
        complaint: 'Broken upper front tooth after a fall',
        diagnosis: 'Complicated crown fracture of {tooth}',
        notes: 'Pulp exposed. Root canal treatment followed by a crown advised.',
        medicines: ['amox', 'ibu']
    },
    rct1: {
        treatments: ['rct'],
        complaint: 'Root canal treatment, first sitting',
        diagnosis: 'Irreversible pulpitis in {tooth}',
        notes: 'Access opening, length measurement, cleaning and shaping done. Temporary filling placed.',
        medicines: ['ibuSos']
    },
    rct2: {
        treatments: ['rct'],
        charge: false,
        complaint: 'Root canal treatment, second sitting',
        diagnosis: 'Root canal treatment in progress on {tooth}',
        notes: 'Canals filled and sealed. Crown advised to protect the tooth. Included in the RCT bill.'
    },
    crown: {
        treatments: ['crown'],
        complaint: 'Crown for the root canal treated tooth',
        diagnosis: 'Root canal treated {tooth}',
        notes: 'Zirconia crown cemented on {tooth}. Bite checked and adjusted.'
    },
    extraction: {
        treatments: ['extraction'],
        complaint: 'Broken back tooth with pain while chewing',
        diagnosis: 'Grossly decayed {tooth}, not restorable',
        notes: 'Extraction done under local anaesthesia. Post-operative instructions given.',
        medicines: ['amox', 'acecl', 'chxPostExtraction'],
        instructions: POST_EXTRACTION_INSTRUCTIONS
    },
    extractionWisdom: {
        treatments: ['extraction'],
        complaint: 'Pain and swelling behind the last lower tooth',
        diagnosis: 'Partially erupted {tooth} with pericoronitis',
        notes: 'Surgical removal of {tooth} done. Sutures placed.',
        medicines: ['amoxClav', 'metro', 'acecl', 'chxPostExtraction'],
        instructions: POST_EXTRACTION_INSTRUCTIONS
    },
    review: {
        treatments: ['checkup'],
        charge: false,
        complaint: 'Post-extraction review',
        diagnosis: 'Healing socket, no sign of infection',
        notes: 'Healing is satisfactory. Sutures removed.'
    },
    filling: {
        treatments: ['filling'],
        complaint: 'Food getting stuck between the teeth',
        diagnosis: 'Caries on {tooth}',
        notes: 'Tooth coloured composite filling done on {tooth}.'
    },
    gum: {
        treatments: ['gum'],
        complaint: 'Loose teeth and bad breath',
        diagnosis: 'Chronic periodontitis',
        notes: 'Deep scaling and root planing done on one arch. Second arch planned.',
        medicines: ['metro', 'chx']
    },
    whitening: {
        treatments: ['whitening'],
        complaint: 'Wants brighter teeth for a family function',
        diagnosis: 'Extrinsic and age related discolouration',
        notes: 'In-office bleaching done. Shade improved from A3 to A1.'
    },
    dentureImpression: {
        treatments: ['denture'],
        complaint: 'Difficulty chewing due to missing teeth',
        diagnosis: 'Partially edentulous upper and lower arches',
        notes: 'Impressions taken for removable partial dentures.'
    },
    dentureFit: {
        treatments: ['denture'],
        charge: false,
        complaint: 'Denture trial and fitting',
        diagnosis: 'Partially edentulous upper and lower arches',
        notes: 'Dentures fitted. Use and cleaning instructions given. Included in the denture bill.'
    },
    bridge: {
        treatments: ['bridge'],
        complaint: 'Missing lower back tooth, wants a fixed replacement',
        diagnosis: 'Missing {tooth}',
        notes: 'Supporting teeth prepared and impression taken for a three unit bridge.'
    }
}

const PATIENTS: SeedPatient[] = [
    {
        name: 'Muhammed Shafi K', gender: 'male', age: 42, place: 'Feroke', phone: '9847012345', address: 'Kunnath House, Chungam, Feroke',
        reg: -168, status: 'completed', history: 'Type 2 diabetes for six years, on Metformin 500 mg', conditions: 'Diabetes', notes: 'Prefers morning appointments',
        visits: [
            { kind: 'consultPulpitis', at: -168, tooth: '46', method: 'cash' },
            { kind: 'rct1', at: -164, tooth: '46', pay: 'partial', paid: 2500, method: 'cash', balanceAt: -157 },
            { kind: 'rct2', at: -157, tooth: '46', next: -143, nextNote: 'Crown cementation on 46' },
            { kind: 'crown', at: -143, tooth: '46', method: 'card', discount: 500 },
            { kind: 'scaling', at: -35 }
        ]
    },
    {
        name: 'Ayisha Rahman', gender: 'female', age: 34, place: 'Ramanattukara', phone: '9446123478', address: 'Rahmath Manzil, Near Juma Masjid, Ramanattukara',
        reg: -12, status: 'in-treatment', history: 'No significant medical history', allergies: 'Penicillin', notes: 'Allergic to penicillin. Prescribe alternatives.', penicillinAllergy: true,
        visits: [
            { kind: 'consultAbscess', at: -12, tooth: '36' },
            { kind: 'rct1', at: -7, tooth: '36', pay: 'partial', paid: 2000, balanceAt: 0, next: 0, nextNote: 'RCT second sitting on 36' },
            { kind: 'rct2', at: 0, tooth: '36', next: 14, nextNote: 'Crown preparation for 36' }
        ]
    },
    {
        name: 'Arjun Menon', gender: 'male', dob: '1998-02-14', place: 'Nadakkavu', phone: '8547234561', address: 'Flat 4B, Skyline Apartments, Nadakkavu',
        reg: -95, status: 'active', history: 'No significant medical history', notes: 'Works night shifts, prefers afternoon slots',
        visits: [
            { kind: 'checkupTravel', at: -95, tooth: '36' },
            { kind: 'filling', at: -90, tooth: '36', next: 0, nextNote: 'Three month review of the filling on 36' }
        ]
    },
    {
        name: 'Lakshmi Nair', gender: 'female', age: 56, place: 'Chevayur', phone: '9495345612', altPhone: '9495345699', address: 'Sreepadam, Chevayur, Kozhikode',
        reg: -210, status: 'in-treatment', history: 'Hypertension, on Amlodipine 5 mg', allergies: 'Sulfa drugs', conditions: 'Hypertension', notes: 'Check blood pressure before procedures',
        visits: [
            { kind: 'checkupScaling', at: -210, method: 'cash' },
            { kind: 'dentureImpression', at: -28, pay: 'partial', paid: 6000, method: 'cash', next: -14, nextNote: 'Denture trial and fitting' },
            { kind: 'dentureFit', at: -14, next: 0, nextNote: 'Denture adjustment and review' }
        ]
    },
    {
        name: 'Joseph Thomas', gender: 'male', dob: '1963-07-02', place: 'Eranhipalam', phone: '9847561234', altPhone: '9847561200', address: 'Thekkekara House, Eranhipalam',
        reg: -240, status: 'active', history: 'Cardiac stent placed in 2019. On Aspirin 75 mg and Atorvastatin.', conditions: 'Heart disease, on blood thinner', notes: 'Consult cardiologist before any extraction',
        visits: [
            { kind: 'checkupScaling', at: -240, method: 'card' },
            { kind: 'extraction', at: -50, tooth: '17', method: 'cash' },
            { kind: 'gum', at: -21, next: 0, nextNote: 'Gum treatment review' }
        ]
    },
    {
        name: 'Anjali Krishnan', gender: 'female', age: 25, place: 'Kunnamangalam', phone: '9061234578', address: 'Krishna Nivas, Kunnamangalam',
        reg: -9, status: 'active', history: 'No significant medical history',
        visits: [
            { kind: 'consultSensitivity', at: -9, tooth: '24, 25' },
            { kind: 'filling', at: -5, tooth: '24, 25', quantity: 2, next: 1, nextNote: 'Review sensitivity after the fillings' }
        ]
    },
    {
        name: 'Abdul Rasheed P', gender: 'male', age: 51, place: 'Koduvally', phone: '9995123467', address: 'Puthiyottil House, Koduvally',
        reg: -190, status: 'in-treatment', history: 'Type 2 diabetes and hypertension. On Metformin and Telmisartan.', conditions: 'Diabetes, Hypertension', notes: 'Keep appointments short',
        visits: [
            { kind: 'checkupScaling', at: -190, method: 'cash' },
            { kind: 'gum', at: -10, next: 4, nextNote: 'Deep scaling for the upper arch' }
        ]
    },
    {
        name: 'Sreeja Pillai', gender: 'female', age: 47, place: 'West Hill', phone: '8086123456', address: 'Gokulam, West Hill, Kozhikode',
        reg: -130, status: 'active', history: 'Hypothyroidism, on Thyroxine 50 mcg', conditions: 'Hypothyroidism',
        visits: [
            { kind: 'checkupScaling', at: -130 },
            { kind: 'filling', at: -120, tooth: '15', method: 'card' }
        ]
    },
    {
        name: 'Vishnu Prasad', gender: 'male', age: 31, place: 'Mavoor', phone: '9539123465', address: 'Vishnu Bhavan, Mavoor',
        reg: -185, status: 'active', history: 'No significant medical history',
        visits: [
            { kind: 'checkupScaling', at: -185 },
            { kind: 'scaling', at: -1 }
        ]
    },
    {
        name: 'Neha Varghese', gender: 'female', dob: '2004-05-20', place: 'Medical College', phone: '7012345698', address: 'Ladies Hostel, Medical College Campus, Kozhikode',
        reg: -200, status: 'active', history: 'No significant medical history', allergies: 'Latex', notes: 'Latex allergy. Use nitrile gloves.',
        visits: [
            { kind: 'checkup', at: -200 },
            { kind: 'filling', at: -196, tooth: '46' }
        ]
    },
    {
        name: 'Harikrishnan K', gender: 'male', age: 38, place: 'Balussery', phone: '9746234589', address: 'Kizhakkayil House, Balussery',
        reg: -80, status: 'active', history: 'Smoker, about ten cigarettes a day', notes: 'Counselled on quitting smoking',
        visits: [
            { kind: 'checkup', at: -80, method: 'cash' },
            { kind: 'extractionWisdom', at: -6, tooth: '38', next: 1, nextNote: 'Suture removal and review' }
        ]
    },
    {
        name: 'Shabna Ashraf', gender: 'female', age: 29, place: 'Mukkam', phone: '9562345671', address: 'Ashraf Manzil, Mukkam',
        reg: -20, status: 'active', history: 'No significant medical history',
        visits: [
            { kind: 'checkupScaling', at: -20 }
        ]
    },
    {
        name: 'Midhun Raj', gender: 'male', age: 35, place: 'Koyilandy', phone: '8129345612', address: 'Raj Nivas, Koyilandy',
        reg: -5, status: 'in-treatment', history: 'No significant medical history',
        visits: [
            { kind: 'consultTrauma', at: -5, tooth: '11', next: -3, nextNote: 'Start root canal treatment on 11' },
            { kind: 'rct1', at: -3, tooth: '11', pay: 'partial', paid: 2500, next: 7, nextNote: 'RCT second sitting on 11' }
        ]
    },
    {
        name: 'Divya Mohan', gender: 'female', age: 40, place: 'Kakkodi', phone: '9447456123', address: 'Mohanam, Kakkodi',
        reg: -75, status: 'completed', history: 'Mild asthma, uses an inhaler when needed', allergies: 'Aspirin', conditions: 'Asthma',
        visits: [
            { kind: 'consultSensitivity', at: -75, tooth: '14, 24' },
            { kind: 'filling', at: -71, tooth: '14, 24', quantity: 2 }
        ]
    },
    {
        name: 'Riyas Ahammed', gender: 'male', age: 27, place: 'Thamarassery', phone: '7907123456', address: 'Ahammed Villa, Thamarassery',
        reg: -9, status: 'in-treatment', history: 'No significant medical history',
        visits: [
            { kind: 'extraction', at: -9, tooth: '37', method: 'cash', next: -2, nextNote: 'Post-extraction review' }
        ]
    },
    {
        name: 'Aleena Joseph', gender: 'female', age: 19, place: 'Beypore', phone: '6238123457', address: 'Puthenpurayil House, Beypore',
        reg: 'm4', status: 'active', history: 'No significant medical history', notes: 'College student, available after 4 PM',
        visits: [
            { kind: 'checkup', at: 'm4', diagnosis: 'Early caries on 26 and 27', next: 10, nextNote: 'Fillings on 26 and 27' }
        ]
    },
    {
        name: 'Ajmal Hussain', gender: 'male', age: 33, place: 'Kondotty', phone: '9633234512', address: 'Hussain Manzil, Kondotty',
        reg: -110, status: 'active', history: 'No significant medical history',
        visits: [
            { kind: 'extractionWisdom', at: -110, tooth: '48', next: -103, nextNote: 'Suture removal' },
            { kind: 'review', at: -103 },
            { kind: 'filling', at: -13, tooth: '47', pay: 'none' }
        ]
    },
    {
        name: 'Gopika Suresh', gender: 'female', age: 24, place: 'Pantheeramkavu', phone: '8590123467', address: 'Suresh Bhavan, Pantheeramkavu',
        reg: 0, status: 'active', history: 'No significant medical history', notes: 'New patient, booked by phone',
        visits: []
    },
    {
        name: 'Mathew Kurian', gender: 'male', dob: '1958-11-09', place: 'Nadakkavu', phone: '9447981234', altPhone: '9447981200', address: 'Kurian Villa, Nadakkavu',
        reg: -230, status: 'active', history: 'Type 2 diabetes and hypertension', conditions: 'Diabetes, Hypertension', notes: 'Wears upper and lower partial dentures',
        visits: [
            { kind: 'dentureImpression', at: -225, method: 'bank-transfer' },
            { kind: 'dentureFit', at: -211 },
            { kind: 'checkupDenture', at: -60, method: 'cash' }
        ]
    },
    {
        name: 'Nandana Rajeev', gender: 'female', age: 16, place: 'Chevayur', phone: '9846234510', altPhone: '9846234511', address: 'Rajeev Nivas, Chevayur',
        reg: -55, status: 'completed', history: 'No significant medical history', notes: 'Minor. Accompanied by her mother, Smitha Rajeev.',
        visits: [
            { kind: 'checkup', at: -55, diagnosis: 'Caries on 16' },
            { kind: 'filling', at: -50, tooth: '16' }
        ]
    },
    {
        name: 'Sajid Ali', gender: 'male', age: 45, place: 'Vadakara', phone: '9895123478', address: 'Ali Manzil, Vadakara',
        reg: -20, status: 'in-treatment', history: 'No significant medical history',
        visits: [
            { kind: 'consultPulpitis', at: -20, tooth: '46', pay: 'none', next: -6, nextNote: 'Start root canal treatment on 46' }
        ]
    },
    {
        name: 'Meera Ramesh', gender: 'female', age: 37, place: 'Perambra', phone: '8547912345', address: 'Ramesh Nivas, Perambra',
        reg: -160, status: 'active', history: 'No significant medical history',
        visits: [
            { kind: 'checkupScaling', at: -155 },
            { kind: 'filling', at: -150, tooth: '26', next: 25, nextNote: 'Six month check-up and cleaning' }
        ]
    },
    {
        name: 'Anwar Sadath', gender: 'male', age: 59, place: 'Kozhikode Beach', phone: '9744123456', address: 'Sadath Manzil, Beach Road, Kozhikode',
        reg: -175, status: 'active', history: 'Smoker for 30 years. Controlled hypertension.', conditions: 'Hypertension', notes: 'Counselled on stopping smoking',
        visits: [
            { kind: 'checkupScaling', at: -175, method: 'cash' },
            { kind: 'gum', at: -168, next: -154, nextNote: 'Gum treatment for the lower arch' },
            { kind: 'gum', at: -154, method: 'card' },
            { kind: 'bridge', at: -66, tooth: '46', pay: 'partial', paid: 10000, method: 'card' }
        ]
    },
    {
        name: 'Reshma Unnikrishnan', gender: 'female', age: 32, place: 'Feroke', phone: '9567234561', address: 'Unnikrishna Nivas, Feroke',
        reg: -140, status: 'active', history: 'Asthma, uses an inhaler', conditions: 'Asthma',
        visits: [
            { kind: 'checkupScaling', at: -140 },
            { kind: 'whitening', at: -100, method: 'card', discount: 10, discountType: 'percent' }
        ]
    },
    {
        name: 'Thomas Chacko', gender: 'male', age: 72, place: 'Eranhipalam', phone: '9447123499', altPhone: '9447123400', address: 'Chacko Villa, Eranhipalam',
        reg: -220, status: 'inactive', history: 'Hypertension and mild arthritis', conditions: 'Hypertension', notes: 'His son usually brings him for visits',
        visits: [
            { kind: 'dentureImpression', at: -220, method: 'cash' },
            { kind: 'dentureFit', at: -205 }
        ]
    },
    {
        name: 'Jasna Basheer', gender: 'female', age: 30, place: 'Kunnamangalam', phone: '9605123478', address: 'Basheer Manzil, Kunnamangalam',
        reg: -45, status: 'in-treatment', history: 'Iron deficiency anaemia', conditions: 'Anaemia',
        visits: [
            { kind: 'checkupBleeding', at: -45 },
            { kind: 'gum', at: -40, pay: 'partial', paid: 1500, next: -12, nextNote: 'Gum treatment for the upper arch' }
        ]
    },
    {
        name: 'Akhil Mohan', gender: 'male', age: 26, place: 'Ramanattukara', phone: '7559123456', address: 'Mohan Nivas, Ramanattukara',
        reg: 'm2', status: 'active', history: 'No significant medical history', notes: 'Booked through WhatsApp',
        visits: []
    },
    {
        name: 'Fathima Nasrin', gender: 'female', age: 21, place: 'Kondotty', phone: '8714123456', address: 'Nasrin Manzil, Kondotty',
        reg: 'm4', status: 'active', history: 'No significant medical history',
        visits: [
            { kind: 'checkup', at: 'm4' }
        ]
    },
    {
        name: 'Rahul Das', gender: 'male', age: 39, place: 'Mukkam', phone: '9048123456', address: 'Das Bhavan, Mukkam',
        reg: -125, status: 'completed', history: 'No significant medical history',
        visits: [
            { kind: 'consultAbscess', at: -125, tooth: '36' },
            { kind: 'rct1', at: -121, tooth: '36' },
            { kind: 'rct2', at: -114, tooth: '36', next: -100, nextNote: 'Crown for 36' },
            { kind: 'crown', at: -100, tooth: '36', method: 'card' }
        ]
    },
    {
        name: 'Sneha George', gender: 'female', age: 44, place: 'West Hill', phone: '9946123457', address: 'George Villa, West Hill',
        reg: 'm6', status: 'active', history: 'Hypothyroidism', conditions: 'Hypothyroidism',
        visits: [
            { kind: 'checkupScaling', at: 'm6' }
        ]
    }
]

const APPOINTMENTS: SeedAppointment[] = [
    { patient: 1, at: 0, time: '09:30', treatment: 'rct', status: 'completed', notes: 'RCT second sitting on 36' },
    { patient: 2, at: 0, time: '10:15', treatment: 'checkup', status: 'in-progress', notes: 'Review of the filling on 36' },
    { patient: 3, at: 0, time: '11:00', treatment: 'denture', status: 'arrived', notes: 'Denture adjustment' },
    { patient: 17, at: 0, time: '12:00', treatment: 'consultation', status: 'confirmed', notes: 'Pain in an upper left back tooth' },
    { patient: 4, at: 0, time: '15:30', treatment: 'gum', status: 'scheduled', notes: 'Gum treatment review' },
    { patient: 9, at: 0, time: '17:00', treatment: 'scaling', status: 'scheduled', notes: 'Six month cleaning' },
    { patient: 5, at: 1, time: '10:00', treatment: 'checkup', status: 'confirmed', notes: 'Review sensitivity after fillings' },
    { patient: 11, at: 1, time: '11:30', treatment: 'whitening', status: 'scheduled', notes: '' },
    { patient: 26, at: 1, time: '16:00', treatment: 'consultation', status: 'scheduled', notes: 'Bleeding gums, first visit' },
    { patient: 18, at: 3, time: '10:30', treatment: 'checkup', status: 'scheduled', notes: 'Denture check' },
    { patient: 6, at: 4, time: '09:30', treatment: 'gum', status: 'confirmed', notes: 'Deep scaling for the upper arch' },
    { patient: 12, at: 7, time: '10:30', treatment: 'rct', status: 'scheduled', notes: 'RCT second sitting on 11' },
    { patient: 23, at: 9, time: '17:30', treatment: 'scaling', status: 'scheduled', notes: '' },
    { patient: 15, at: 10, time: '16:30', treatment: 'filling', status: 'scheduled', notes: 'Fillings on 26 and 27' },
    { patient: 8, at: -1, time: '10:00', treatment: 'scaling', status: 'completed', notes: '' },
    { patient: 14, at: -2, time: '11:00', treatment: 'checkup', status: 'no-show', notes: 'Post-extraction review' },
    { patient: 7, at: -3, time: '15:00', treatment: 'whitening', status: 'cancelled', notes: 'Patient asked to postpone' },
    { patient: 5, at: -5, time: '14:00', treatment: 'filling', status: 'completed', notes: 'Fillings on 24 and 25' }
]

const MESSAGES: SeedMessage[] = [
    { patient: 5, kind: 'appointmentConfirmation', at: -5, time: '14:40', status: 'sent', appointment: 6 },
    { patient: 6, kind: 'appointmentConfirmation', at: -2, time: '11:15', status: 'sent', appointment: 10 },
    { patient: 14, kind: 'followUpReminder', at: -1, time: '10:30', status: 'sent', followUpOfPatient: true },
    { patient: 25, kind: 'followUpReminder', at: -13, time: '17:05', status: 'sent', followUpOfPatient: true },
    { patient: 11, kind: 'appointmentReminder', at: 0, time: '09:10', status: 'ready', appointment: 7 },
    { patient: 3, kind: 'paymentReminder', at: -3, time: '18:20', status: 'draft', invoiceOfPatient: true }
]

const VISIT_TIMES = ['10:20', '11:05', '12:10', '15:40', '16:25', '17:15']

const fill = (text: string, tooth?: string): string => text.replace(/\{tooth\}/g, tooth || 'the tooth')

export const createSeedData = (today: string): ClinicData => {
    const dayOfMonth = parseISODate(today).getDate()

    const resolve = (offset: Offset): string => {
        if (typeof offset === 'string') {
            const back = Math.min(Number(offset.slice(1)) || 0, dayOfMonth - 1)
            const date = addDays(today, -back)
            return dayOfWeek(date) === 0 && back > 1 ? addDays(date, 1) : date
        }
        const date = addDays(today, offset)
        if (Math.abs(offset) >= 2 && dayOfWeek(date) === 0) return addDays(date, offset < 0 ? -1 : 1)
        return date
    }

    let sequence = 0
    const nextId = (prefix: string): string => {
        sequence += 1
        return `${prefix}_${String(sequence).padStart(4, '0')}`
    }

    const settings = JSON.parse(JSON.stringify(DEFAULT_SETTINGS)) as typeof DEFAULT_SETTINGS
    const seedStamp = stampAt(today, '08:00')

    // Treatments
    const treatments: Treatment[] = DEFAULT_TREATMENTS.map(({ key, ...treatment }) => ({
        ...treatment,
        id: `trt_${key}`,
        createdAt: seedStamp,
        updatedAt: seedStamp
    }))
    const treatmentByKey = new Map(DEFAULT_TREATMENTS.map(item => [item.key as TreatmentKey, treatments.find(treatment => treatment.id === `trt_${item.key}`)!]))

    // Patients, codes follow registration order
    const registrationOrder = PATIENTS
        .map((seed, index) => ({ index, date: resolve(seed.reg) }))
        .sort((a, b) => a.date.localeCompare(b.date) || a.index - b.index)

    const codeByIndex = new Map<number, string>()
    registrationOrder.forEach((entry, position) => codeByIndex.set(entry.index, formatPatientCode(1001 + position)))

    const patients: Patient[] = PATIENTS.map((seed, index) => {
        const registeredOn = resolve(seed.reg)
        const stamp = stampAt(registeredOn, '09:45')
        return {
            id: `pt_${String(index + 1).padStart(2, '0')}`,
            code: codeByIndex.get(index) ?? formatPatientCode(1001 + index),
            name: seed.name,
            phone: seed.phone,
            altPhone: seed.altPhone ?? '',
            dob: seed.dob ?? '',
            age: seed.dob ? null : (seed.age ?? null),
            gender: seed.gender,
            address: seed.address,
            place: seed.place,
            registeredOn,
            medicalHistory: seed.history,
            allergies: seed.allergies ?? '',
            conditions: seed.conditions ?? '',
            notes: seed.notes ?? '',
            status: seed.status,
            createdAt: stamp,
            updatedAt: stamp
        }
    })

    // Appointments
    const appointments: Appointment[] = APPOINTMENTS.map(seed => {
        const patient = patients[seed.patient]
        const treatment = treatmentByKey.get(seed.treatment)!
        const date = resolve(seed.at)
        const bookedOn = seed.at === 0 ? addDays(today, -3) : addDays(date, -4)
        const stamp = stampAt(bookedOn, '12:30')
        return {
            id: nextId('apt'),
            patientId: patient.id,
            phone: patient.phone,
            date,
            time: seed.time,
            duration: treatment.duration,
            treatmentId: treatment.id,
            treatmentName: treatment.name,
            notes: seed.notes,
            status: seed.status,
            createdAt: stamp,
            updatedAt: stamp
        }
    })

    // Visits, bills, prescriptions and follow-ups
    const visits: Visit[] = []
    const draftInvoices: Array<Omit<Invoice, 'number'>> = []
    const draftPrescriptions: Array<Omit<Prescription, 'number'>> = []
    const followUps: FollowUp[] = []

    PATIENTS.forEach((seed, patientIndex) => {
        const patient = patients[patientIndex]
        const resolvedVisits = seed.visits.map(visit => ({ seed: visit, date: resolve(visit.at) }))

        resolvedVisits.forEach(({ seed: visitSeed, date }, visitIndex) => {
            const template = TEMPLATES[visitSeed.kind]
            const time = VISIT_TIMES[(patientIndex + visitIndex) % VISIT_TIMES.length]
            const stamp = stampAt(date, time)
            const visitId = nextId('vis')
            const charge = template.charge !== false
            const quantity = visitSeed.quantity ?? 1
            const treatmentRefs = template.treatments.map(key => {
                const treatment = treatmentByKey.get(key)!
                return { treatmentId: treatment.id, name: treatment.name }
            })

            let medicineKeys = template.medicines ?? []
            if (seed.penicillinAllergy) {
                medicineKeys = medicineKeys.map(key => (key === 'amox' || key === 'amoxClav' ? 'clinda' : key))
            }
            const medicines: Medicine[] = medicineKeys.map(key => ({ ...MEDICINES[key], id: nextId('med') }))
            const prescriptionNotes = medicines.length
                ? medicines.map(medicine => `${medicine.name} (${medicine.duration})`).join(', ')
                : template.prescriptionNotes ?? ''

            const linkedAppointment = appointments.find(item => item.patientId === patient.id && item.date === date && item.status === 'completed')
            const nextVisitDate = visitSeed.next !== undefined ? resolve(visitSeed.next) : ''

            const visit: Visit = {
                id: visitId,
                patientId: patient.id,
                appointmentId: linkedAppointment?.id ?? null,
                date,
                complaint: template.complaint,
                diagnosis: visitSeed.diagnosis ?? fill(template.diagnosis, visitSeed.tooth),
                treatments: treatmentRefs,
                toothNumbers: visitSeed.tooth ?? '',
                notes: fill(template.notes, visitSeed.tooth),
                prescriptionNotes,
                nextVisitDate,
                cost: 0,
                paymentStatus: 'paid',
                createdAt: stamp,
                updatedAt: stamp
            }

            if (charge) {
                const items = template.treatments.map(key => {
                    const treatment = treatmentByKey.get(key)!
                    return {
                        id: nextId('item'),
                        treatmentId: treatment.id,
                        description: treatment.name,
                        unitPrice: treatment.price,
                        quantity: key === 'filling' ? quantity : 1
                    }
                })
                const invoice: Omit<Invoice, 'number'> = {
                    id: nextId('inv'),
                    patientId: patient.id,
                    visitId,
                    date,
                    items,
                    discountType: visitSeed.discountType ?? 'amount',
                    discountValue: visitSeed.discount ?? 0,
                    taxRate: 0,
                    taxLabel: settings.billing.taxLabel,
                    payments: [],
                    notes: visitSeed.tooth ? `Tooth ${visitSeed.tooth}` : '',
                    createdAt: stamp,
                    updatedAt: stamp
                }
                const total = getInvoiceSummary(invoice).total
                const method = visitSeed.method ?? 'upi'
                const pay = visitSeed.pay ?? 'full'

                if (pay === 'full') {
                    invoice.payments.push({ id: nextId('pay'), date, amount: total, method, reference: '', note: '', createdAt: stamp })
                }
                if (pay === 'partial') {
                    const paid = Math.min(visitSeed.paid ?? Math.round(total / 2), total)
                    invoice.payments.push({ id: nextId('pay'), date, amount: paid, method, reference: '', note: 'Advance payment', createdAt: stamp })
                    if (visitSeed.balanceAt !== undefined) {
                        const balanceDate = resolve(visitSeed.balanceAt)
                        invoice.payments.push({ id: nextId('pay'), date: balanceDate, amount: total - paid, method: 'upi', reference: '', note: 'Balance payment', createdAt: stampAt(balanceDate, '10:40') })
                    }
                }

                const summary = getInvoiceSummary(invoice)
                visit.cost = summary.total
                visit.paymentStatus = summary.status
                draftInvoices.push(invoice)
            }

            if (medicines.length) {
                draftPrescriptions.push({
                    id: nextId('rx'),
                    patientId: patient.id,
                    visitId,
                    date,
                    chiefComplaint: visit.complaint,
                    diagnosis: visit.diagnosis,
                    medicines,
                    instructions: template.instructions ?? settings.prescription.defaultInstructions,
                    notes: visitSeed.nextNote ? `Next visit: ${visitSeed.nextNote}.` : 'Review if symptoms persist.',
                    followUpDate: nextVisitDate,
                    createdAt: stamp,
                    updatedAt: stamp
                })
            }

            if (nextVisitDate) {
                const later = resolvedVisits.slice(visitIndex + 1).find(entry => entry.date >= date)
                const history: FollowUpEvent[] = [{ id: nextId('evt'), at: stamp, type: 'created', note: `Next visit set for ${visitSeed.nextNote ?? 'review'}` }]
                if (later) {
                    history.push({ id: nextId('evt'), at: stampAt(later.date, '11:30'), type: 'completed', note: 'Patient returned for the planned visit' })
                }
                followUps.push({
                    id: nextId('fu'),
                    patientId: patient.id,
                    visitId,
                    treatment: treatmentRefs.map(ref => ref.name).join(', '),
                    previousVisitDate: date,
                    dueDate: nextVisitDate,
                    notes: visitSeed.nextNote ?? '',
                    status: later ? 'completed' : 'pending',
                    completedOn: later ? later.date : '',
                    reminderCount: 0,
                    lastReminderAt: '',
                    history,
                    createdAt: stamp,
                    updatedAt: later ? stampAt(later.date, '11:30') : stamp
                })
            }

            visits.push(visit)
        })
    })

    // Number bills and prescriptions in date order
    const invoices: Invoice[] = [...draftInvoices]
        .sort((a, b) => a.date.localeCompare(b.date) || a.createdAt.localeCompare(b.createdAt))
        .map((invoice, index) => ({ ...invoice, number: formatInvoiceNumber(settings.billing.invoicePrefix, index + 1, invoice.date) }))

    const prescriptions: Prescription[] = [...draftPrescriptions]
        .sort((a, b) => a.date.localeCompare(b.date) || a.createdAt.localeCompare(b.createdAt))
        .map((prescription, index) => ({ ...prescription, number: formatPrescriptionNumber(index + 1, prescription.date) }))

    // WhatsApp message log
    const messages: MessageLog[] = MESSAGES.map(seed => {
        const patient = patients[seed.patient]
        const appointment = seed.appointment !== undefined ? appointments[seed.appointment] : null
        const followUp = seed.followUpOfPatient ? followUps.find(item => item.patientId === patient.id && item.status === 'pending') ?? null : null
        const invoice = seed.invoiceOfPatient ? invoices.find(item => item.patientId === patient.id && getInvoiceSummary(item).balance > 0) ?? null : null
        const date = resolve(seed.at)
        const stamp = stampAt(date, seed.time)
        const template = seed.kind === 'custom' ? '' : settings.whatsapp.templates[seed.kind]
        const body = fillTemplate(template, buildTemplateValues({ patient, appointment, followUp, invoice, settings }))

        if (followUp && seed.status === 'sent') {
            followUp.reminderCount += 1
            followUp.lastReminderAt = stamp
            followUp.history.push({ id: nextId('evt'), at: stamp, type: 'reminder', note: 'WhatsApp reminder opened for sending' })
        }

        return {
            id: nextId('msg'),
            patientId: patient.id,
            phone: patient.phone,
            kind: seed.kind,
            body,
            status: seed.status,
            appointmentId: appointment?.id ?? null,
            followUpId: followUp?.id ?? null,
            invoiceId: invoice?.id ?? null,
            createdAt: stamp,
            sentAt: seed.status === 'sent' ? stamp : ''
        }
    })

    return {
        version: DATA_VERSION,
        seededOn: today,
        patients,
        treatments,
        visits,
        appointments,
        prescriptions,
        invoices,
        followUps,
        messages,
        settings,
        counters: {
            patient: 1000 + patients.length,
            prescription: prescriptions.length,
            invoice: invoices.length
        }
    }
}
